import { useCallback, useEffect, useState } from "react";
import { Trash2, CheckCircle2, RotateCcw, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Panel, Spinner, EmptyState, ConfirmDialog, btnGhost, btnDanger, fmtDateTime } from "@/components/portal/ui";

const PROFILE = {
  medico: { label: "Medico", color: "#5B2C91" },
  infermiere: { label: "Infermiere", color: "#0EA5E9" },
  farmacia: { label: "Farmacia", color: "#10B981" },
  struttura: { label: "Struttura / Altro", color: "#FF7EB3" },
};

const SCOPES = [
  { key: "all", label: "Tutte" },
  { key: "new", label: "Nuove" },
  { key: "handled", label: "Gestite" },
];

export default function AdminContactRequests() {
  const [items, setItems] = useState(null);
  const [scope, setScope] = useState("all");
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/contact-requests").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (item, status) => {
    try {
      await api.put(`/admin/contact-requests/${item.id}`, { status });
      toast.success(status === "handled" ? "Segnata come gestita" : "Riaperta");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/contact-requests/${toDelete.id}`);
      toast.success("Richiesta eliminata");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const filtered = (items || []).filter((i) => scope === "all" || i.status === scope);
  const newCount = (items || []).filter((i) => i.status === "new").length;

  return (
    <div data-testid="admin-contact-requests">
      <PageHeader
        eyebrow="Candidature"
        title="Richieste di collaborazione"
        desc="Le richieste inviate dalla sezione “Lavora con noi” del sito (medici, infermieri, farmacie e strutture). Ogni invio arriva anche via email a info@tytocare.it."
      />

      <div className="mb-5 inline-flex rounded-full bg-white p-1 ring-1 ring-[#5B2C91]/10" data-testid="contact-scope-tabs">
        {SCOPES.map((s) => (
          <button key={s.key} onClick={() => setScope(s.key)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${scope === s.key ? "bg-[#5B2C91] text-white" : "text-[#4A4059] hover:text-[#5B2C91]"}`} data-testid={`contact-scope-${s.key}`}>
            {s.label}{s.key === "new" && newCount > 0 ? ` (${newCount})` : ""}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState title="Nessuna richiesta" desc="Le candidature e le richieste di collaborazione dal sito compariranno qui." />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {filtered.map((r) => {
            const p = PROFILE[r.profile] || { label: r.profile, color: "#5B2C91" };
            return (
              <Panel key={r.id} className="flex flex-col gap-3" data-testid={`contact-req-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold" style={{ backgroundColor: `${p.color}18`, color: p.color }}>{p.label}</span>
                  <div className="flex items-center gap-2">
                    {r.status === "new" ? (
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">Nuova</span>
                    ) : (
                      <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">Gestita</span>
                    )}
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.email_status === "sent" ? "bg-[#5B2C91]/10 text-[#5B2C91]" : r.email_status === "failed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`} title={r.email_error || ""}>
                      {r.email_status === "sent" ? "Email inviata" : r.email_status === "failed" ? "Email non inviata" : "Email —"}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="font-heading text-lg font-semibold text-[#1F152E]">{r.name}</p>
                  <div className="mt-1 flex flex-col gap-0.5 text-sm text-[#6B6280]">
                    <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 hover:text-[#5B2C91]" data-testid={`contact-req-email-${r.id}`}><Mail className="h-3.5 w-3.5" /> {r.email}</a>
                    {r.phone && <a href={`tel:${r.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-[#5B2C91]"><Phone className="h-3.5 w-3.5" /> {r.phone}</a>}
                  </div>
                </div>

                <p className="whitespace-pre-wrap rounded-2xl bg-[#F6F4FA] px-4 py-3 text-sm text-[#1F152E]">{r.message}</p>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#8A809A]">{fmtDateTime(r.created_at)}</span>
                  <div className="flex items-center gap-1">
                    {r.status === "new" ? (
                      <button onClick={() => setStatus(r, "handled")} className={btnGhost} data-testid={`contact-req-handle-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Segna gestita</button>
                    ) : (
                      <button onClick={() => setStatus(r, "new")} className={btnGhost} data-testid={`contact-req-reopen-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /> Riapri</button>
                    )}
                    <button onClick={() => setToDelete(r)} className={btnDanger} aria-label="Elimina" data-testid={`contact-req-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
      )}

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Eliminare la richiesta?" desc={toDelete ? `${toDelete.name} · ${toDelete.email}` : ""} onConfirm={remove} confirmLabel="Elimina" destructive />
    </div>
  );
}
